import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { notification, Progress, Statistic, Space } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { apiUrl } from "app.config";
import { CfdiFixProgress } from "api/generated";

export interface WebSocketNotificationOptions {
  onComplete?: () => void;
  onError?: (error: unknown) => void;
}

export interface UseWebSocketNotificationReturn {
  startNotification: (processId: string) => void;
  isActive: boolean;
}

const NOTIFICATION_KEY = "cfdi-fix-notification";

const NotificationContent = ({ progress }: { progress: CfdiFixProgress }) => {
  const getStatusColor = () => {
    switch (progress.status) {
      case "COMPLETED":
        return "#52c41a";
      case "ERROR":
        return "#ff4d4f";
      default:
        return "#1890ff";
    }
  };

  const getStatusIcon = () => {
    switch (progress.status) {
      case "COMPLETED":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "ERROR":
        return <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />;
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (progress.status) {
      case "STARTED":
        return "Iniciando asociación de CFDIs";
      case "PROCESSING":
        return "Asociación en progreso";
      case "COMPLETED":
        return "Asociación completada";
      case "ERROR":
        return "Error en asociación";
      default:
        return "Asociación de CFDIs";
    }
  };

  return (
    <div style={{ width: 380, minHeight: 180 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        <Space align="center">
          {getStatusIcon()}
          <span style={{ fontWeight: 600, fontSize: 16 }}>{getTitle()}</span>
        </Space>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 20,
          marginBottom: 16,
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <Progress
            type="circle"
            percent={Number(progress.percentage?.toFixed(1))}
            status={
              progress.status === "ERROR"
                ? "exception"
                : progress.status === "COMPLETED"
                  ? "success"
                  : "normal"
            }
            strokeColor={getStatusColor()}
          />
        </div>

        <div style={{ flex: 1 }}>
          <Statistic
            title={progress.currentOperation || "Procesando..."}
            value={progress.cfdisProcessed || 0}
            suffix={`/ ${progress.totalCfdis || "?"}`}
            valueStyle={{
              fontSize: 24,
              fontWeight: 600,
              color: getStatusColor(),
            }}
          />
          <div
            style={{
              display: "flex",
              gap: 16,
              marginTop: 8,
              fontSize: 12,
              minHeight: 20,
            }}
          >
            {progress.cfdisAssigned !== undefined && (
              <div>
                <span style={{ color: "#52c41a", fontWeight: 500 }}>
                  ✓ {progress.cfdisAssigned}
                </span>{" "}
                asignados
              </div>
            )}
            {progress.cfdisNotFound !== undefined && (
              <div>
                <span style={{ color: "#fa8c16", fontWeight: 500 }}>
                  ✗ {progress.cfdisNotFound}
                </span>{" "}
                no asociados
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ minHeight: 44 }}>
        {progress.message && (
          <div
            style={{
              padding: "10px 16px",
              backgroundColor:
                progress.status === "ERROR" ? "#fff2f0" : "#f6ffed",
              border: `1px solid ${progress.status === "ERROR" ? "#ffccc7" : "#b7eb8f"}`,
              borderRadius: 6,
              color: progress.status === "ERROR" ? "#cf1322" : "#389e0d",
            }}
          >
            {progress.message}
          </div>
        )}
      </div>
    </div>
  );
};

export const useWebSocketCFDIFix = (
  options: WebSocketNotificationOptions = {},
): UseWebSocketNotificationReturn => {
  const [isActive, setIsActive] = useState(false);
  const isActiveRef = useRef(false);
  const onCompleteRef = useRef(options.onComplete);
  const onErrorRef = useRef(options.onError);

  // ---------------------------------------------------------------------------
  // El processId más reciente — usado para suscribirse a los topics correctos
  // cuando el cliente conecte (o reconecte)
  // ---------------------------------------------------------------------------
  const processIdRef = useRef<string | null>(null);
  const stompClientRef = useRef<Client | null>(null);

  // Timeout de inactividad — si no llega ningún mensaje en 3s, apagamos el loading
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onCompleteRef.current = options.onComplete;
  }, [options.onComplete]);

  useEffect(() => {
    onErrorRef.current = options.onError;
  }, [options.onError]);

  const setActive = useCallback((val: boolean) => {
    isActiveRef.current = val;
    setIsActive(val);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      if (isActiveRef.current) {
        setActive(false);
        onCompleteRef.current?.();
      }
    }, 3000);
  }, [setActive]);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const webSocketUrl = useMemo(
    () => `${apiUrl.replace(/\/$/, "")}/volumetric-control`,
    [],
  );

  const showNotification = useCallback((progress: CfdiFixProgress) => {
    notification.open({
      key: NOTIFICATION_KEY,
      message: null,
      description: <NotificationContent progress={progress} />,
      duration:
        progress.status === "COMPLETED" || progress.status === "ERROR" ? 8 : 0,
      style: { width: 440 },
    });
  }, []);

  useEffect(() => {
    const socket = new SockJS(webSocketUrl);
    const stompClient = new Client({
      webSocketFactory: () => socket,
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      onConnect: () => {
        if (processIdRef.current) {
          doSubscribe(stompClient, processIdRef.current);
        }
      },
      onStompError: (frame) => console.error("STOMP error:", frame),
      onWebSocketError: (ev) => console.error("WebSocket error:", ev),
    });

    stompClient.activate();
    stompClientRef.current = stompClient;

    return () => {
      stompClient.deactivate();
      stompClientRef.current = null;
    };
  }, [webSocketUrl]);

  const doSubscribe = useCallback(
    (client: Client, processId: string) => {
      const handle = (progress: CfdiFixProgress) => {
        showNotification(progress);
        if (progress.status === "COMPLETED") {
          clearInactivityTimer();
          setActive(false);
          onCompleteRef.current?.();
        } else {
          // Reiniciar el timer con cada mensaje
          resetInactivityTimer();
        }
      };

      const handleErr = (errorData: CfdiFixProgress) => {
        clearInactivityTimer();
        setActive(false);
        showNotification({
          ...errorData,
          status: "ERROR",
          message: errorData.message || "Ocurrió un error en el proceso",
        });
        onErrorRef.current?.(errorData);
      };

      client.subscribe(`/topic/cfdi-fix/progress/${processId}`, (msg) => {
        try {
          handle(JSON.parse(msg.body));
        } catch (e) {
          console.error(e);
        }
      });
      client.subscribe(`/topic/cfdi-fix/complete/${processId}`, (msg) => {
        try {
          handle(JSON.parse(msg.body));
        } catch (e) {
          console.error(e);
        }
      });
      client.subscribe(`/topic/cfdi-fix/error/${processId}`, (msg) => {
        try {
          handleErr(JSON.parse(msg.body));
        } catch (e) {
          console.error(e);
        }
      });
    },
    [showNotification, setActive, resetInactivityTimer, clearInactivityTimer],
  );

  const startNotification = useCallback(
    (processId: string) => {
      if (!processId) return;

      processIdRef.current = processId;
      setActive(true);
      resetInactivityTimer(); // empieza a contar desde que se llama startNotification

      const client = stompClientRef.current;
      if (client?.connected) {
        doSubscribe(client, processId);
      }
    },
    [doSubscribe, setActive, resetInactivityTimer],
  );

  // Limpiar el timer si el componente se desmonta mientras está activo
  useEffect(() => () => clearInactivityTimer(), [clearInactivityTimer]);

  return { startNotification, isActive };
};
